import React, { useState, useEffect, useRef } from 'react';
import TagChip from './TagChip';
import { fetchVocabulary, usageCounts, matchTags } from '../lib/tags';

// Filter-by-tag bar for a list (supplier directory / briefs list). AND
// semantics: a record matches only if it carries every selected tag. Renders
// the selected tags as removable chips, an "+ Add filter" popover (non-archived
// tags, no create flow), a live match count, and Clear. The parent owns the
// selected-id array and applies the actual filtering.
// `bare` drops the card chrome for callers that already sit INSIDE a bordered
// panel — otherwise the bar draws a second box inside the first, which is what
// made the tag filter look like a nested sub-card next to Category and Search
// on Create New Brief. Opt-in, so every standalone use keeps its card.
export default function TagFilterBar({ vocabulary, selectedIds, onChange, matchCount, noun = 'result', bare = false }) {
  const [vocab, setVocab] = useState([]);
  const [usage, setUsage] = useState(new Map());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [v, u] = await Promise.all([
        fetchVocabulary(vocabulary, { includeArchived: false }),
        usageCounts(vocabulary),
      ]);
      if (cancelled) return;
      setVocab(v); setUsage(u);
    })();
    return () => { cancelled = true; };
  }, [vocabulary]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const selected = selectedIds.map(id => vocab.find(t => t.id === id)).filter(Boolean);
  const selectableBase = vocab.filter(t => !selectedIds.includes(t.id));
  const options = query
    ? matchTags(query, selectableBase, usage).map(m => ({ tag: m.tag, start: m.start, len: m.len }))
    : [...selectableBase].sort((a, b) => (usage.get(b.id) || 0) - (usage.get(a.id) || 0)).map(t => ({ tag: t }));

  const add = (id) => { onChange([...selectedIds, id]); setQuery(''); };
  const remove = (id) => onChange(selectedIds.filter(x => x !== id));

  return (
    <div className={`flex items-center gap-3 flex-wrap ${bare ? '' : 'bg-white border border-line rounded-xl px-4 py-3 mb-4'}`}>
      <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A]">Filter by tag:</span>
      <div className="flex items-center gap-1.5 flex-wrap flex-1 relative" ref={ref}>
        {selected.map(t => <TagChip key={t.id} tag={t} onRemove={() => remove(t.id)} />)}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 border border-dashed border-line text-[#51534A] hover:text-fusion-mid-grey hover:border-[#DADCCD] text-[11px] font-bold font-['Metropolis'] transition-colors"
        >
          + Add filter
        </button>
        {open && (
          <div className="absolute z-30 top-full left-0 mt-2 w-72 bg-white border border-line rounded-xl p-3 shadow-[0_12px_32px_rgba(0,0,0,0.5)]">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search tags…"
              className="w-full bg-white border border-line rounded-md px-3 py-2 text-xs text-ink outline-none focus:border-[#92D400] mb-3 font-['Metropolis']"
            />
            {options.length === 0 ? (
              <p className="text-[11px] text-[#51534A] italic font-['Metropolis']">No tags to add.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto">
                {options.map(({ tag, start, len }) => (
                  <TagChip key={tag.id} tag={tag} highlight={start != null ? { start, len } : undefined} onClick={() => add(tag.id)} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {selectedIds.length > 0 && (
        <>
          <span className="font-['Metropolis'] text-[11px] font-extrabold text-fusion-forest whitespace-nowrap">{matchCount} {noun}{matchCount === 1 ? '' : 's'} match</span>
          <button type="button" onClick={() => onChange([])} className="font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors">Clear</button>
        </>
      )}
    </div>
  );
}
